import type { Building } from "@/lib/api/building";

// Fetch a per-building list and flatten it, settling each building **independently**: one
// building's failure (e.g. a 500) yields an empty list for that building rather than rejecting the
// whole Promise.all and blanking the entire feed/rail — and never leaves an unhandled rejection
// (#194). Mirrors the per-item `.catch` pattern in DataQualitySection / IndoorConditionsSection /
// EquipmentPerformanceSection.
export async function settlePerBuilding<T>(
  buildings: Building[],
  fetchOne: (building: Building) => Promise<T[]>,
): Promise<T[]> {
  const perBuilding = await Promise.all(
    buildings.map((b) => fetchOne(b).catch(() => [] as T[])),
  );
  return perBuilding.flat();
}

export interface ProgressiveFeedMeta {
  settledCount: number;
  totalCount: number;
}

export interface ProgressiveFeedHandle {
  /** Stop delivering further `onBuildingSettled` calls (e.g. on unmount / re-scope). */
  cancel: () => void;
}

// The portfolio-loading half of the isolation story (story #964/#967): like `settlePerBuilding`,
// one building's failure never affects another — but here each building's rows are delivered to
// `onBuildingSettled` as soon as *that* building resolves, instead of waiting for every building
// via `Promise.all`. A slow/hanging building therefore never stalls the others' rows from
// rendering. Shared by ActivityFeed, FindingsList (uncontrolled/multi-building mode), and
// RecommendationFeed so the isolated + non-blocking behavior lives in one place, not three.
export function loadPerBuildingProgressive<T>(
  buildings: Building[],
  fetchOne: (building: Building) => Promise<T[]>,
  onBuildingSettled: (rows: T[], meta: ProgressiveFeedMeta) => void,
): ProgressiveFeedHandle {
  let cancelled = false;
  let settledCount = 0;
  const totalCount = buildings.length;
  for (const building of buildings) {
    fetchOne(building)
      .catch(() => [] as T[])
      .then((rows) => {
        if (cancelled) return;
        settledCount += 1;
        onBuildingSettled(rows, { settledCount, totalCount });
      });
  }
  return {
    cancel: () => {
      cancelled = true;
    },
  };
}
